import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import type { Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { TrackOptions, type TrackOption } from "#shared/reviewee-filters/trackOptions.ts";
import { Button } from "@/components/ui/button.tsx";
import { ListBox, ListBoxItem } from "@/components/ui/list-box.tsx";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio.tsx";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { FilterPanelBody } from "./FilterPanelBody.tsx";
import { FilterPanelHeader } from "./FilterPanelHeader.tsx";

export interface TrackFilterPanelProps {
    selectedTracks: TrackOption[];
    onSelectionChange: (tracks: TrackOption[]) => void;
    onBack: () => void;
    selectionMode?: "single" | "multiple";
    maxSelections?: number;
}

function toTrackOptions(values: Iterable<string>) {
    return [...values].flatMap(value => TrackOptions.filter(option => option === value));
}

export function TrackFilterPanel({ selectedTracks, onSelectionChange, onBack, selectionMode = "single", maxSelections }: TrackFilterPanelProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("TrackFilterPanel", { i18n: i18nextInstance });
    const { t: tOptions } = useTranslation("TrackOptions", { i18n: i18nextInstance });

    const isAtCap = maxSelections !== undefined && selectedTracks.length >= maxSelections;

    const handleRadioChange = useCallback((value: string) => onSelectionChange(toTrackOptions([value])), [onSelectionChange]);
    const handleClear = useCallback(() => onSelectionChange([]), [onSelectionChange]);

    const handleListChange = useCallback(
        (keys: Selection) => {
            const next = toTrackOptions(keys === "all" ? TrackOptions : [...keys].map(String));

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [onSelectionChange, maxSelections]
    );

    const tracks = useMemo(() => TrackOptions.map(option => ({ id: option, label: tOptions(option) })), [tOptions]);

    return (
        <FilterPanelBody className="w-72">
            <FilterPanelHeader title={t("title")} onBack={onBack} backLabel={t("back")} />
            {selectionMode === "multiple" ? (
                <ListBox aria-label={t("title")} selectionMode="multiple" selectedKeys={selectedTracks} onSelectionChange={handleListChange} escapeKeyBehavior="none" items={tracks} className="max-h-64 min-h-0 border-0">
                    {track => (
                        <ListBoxItem id={track.id} textValue={track.label} isDisabled={isAtCap && !selectedTracks.includes(track.id)}>
                            {track.label}
                        </ListBoxItem>
                    )}
                </ListBox>
            ) : (
                <>
                    <RadioGroup aria-label={t("title")} value={selectedTracks[0] ?? null} onChange={handleRadioChange}>
                        {tracks.map(track => (
                            <RadioField key={track.id} value={track.id}>
                                <Radio>{track.label}</Radio>
                            </RadioField>
                        ))}
                    </RadioGroup>
                    {selectedTracks.length > 0 && (
                        <Button intent="plain" size="xs" onPress={handleClear} className="text-muted-fg hover:text-fg self-start">
                            {t("clear")}
                        </Button>
                    )}
                </>
            )}
        </FilterPanelBody>
    );
}
